import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

interface IEntityPaneHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailingContent?: React.ReactNode;
  avatar?: React.ReactNode;
  /**
   * Optional row rendered inside the header, below the title row (e.g. a tab
   * bar). Because it lives inside the header, it stays pinned with the title
   * and the scroll-driven drop shadow falls below it instead of between it and
   * the title.
   */
  belowContent?: React.ReactNode;
}

/**
 * Title row that sits above EntityPaneBody. Mirrors the Figma "Section Header"
 * component: title in `text-xl` semibold on mobile and `text-display-sm`
 * semibold at md+, mobile container `py-2` (8px each), md+ container
 * `pt-8 pb-3` (32px / 12px).
 *
 * At lg+ the top padding shrinks smoothly from 48px → 16px and a drop shadow
 * fades in as EntityPaneBody is scrolled. Both are driven by
 * `--entity-header-scroll-progress` (0..1), set by EntityPaneBody's scroll
 * handler on EntityPane's wrapper. Below lg the scroll-driven behavior is off.
 *
 * The avatar+text wrapper uses `items-start` so the avatar tops with the title
 * when the subtitle wraps to multiple lines (mobile Account variant). The outer
 * flex stays `items-center` so the trailing actions cluster vertically centers
 * against the avatar+text block.
 *
 * `relative z-10` raises the header above its sibling EntityPaneBody so the
 * drop shadow renders OVER body content as it scrolls beneath. Without a
 * stacking context the body's cards paint after the header in DOM order and
 * clip the shadow.
 *
 * `belowContent` (e.g. a tab bar) renders inside the header below the title
 * row, so it pins with the header and the drop shadow falls below it.
 */
const EntityPaneHeader = ({ title, subtitle, trailingContent, avatar, belowContent }: IEntityPaneHeaderProps) => {
  return (
    <div
      className={cx(
        'lg:entity-header-scroll-shrink relative z-10 flex w-full flex-col px-4 pt-2 md:pt-8 lg:px-8',
        // belowContent (e.g. a tab bar) owns the header's bottom edge so its
        // underline sits flush against the scroll shadow — drop the gap.
        belowContent ? 'pb-0' : 'pb-2 md:pb-3'
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {avatar && <div className="flex shrink-0 items-start">{avatar}</div>}
          <div className="flex min-w-0 flex-col">
            <div className="text-primary md:text-display-sm truncate text-xl font-semibold">{title}</div>
            {subtitle && <div className="text-tertiary text-sm font-normal">{subtitle}</div>}
          </div>
        </div>
        {trailingContent && <div className="text-secondary flex shrink-0 text-sm">{trailingContent}</div>}
      </div>
      {belowContent && <div className="mt-5">{belowContent}</div>}
    </div>
  );
};

export default EntityPaneHeader;
